import {
  NANO_BANANA_2_1_CREDITS,
  NANO_BANANA_2_CREDITS,
  NANO_BANANA_2_LITE_CREDITS,
  NANO_BANANA_PRO_CREDITS,
} from '@/constants/nanoBananaCredits';

export const MIXPANEL_TOKEN = '2c111028b9900d40d81351724bd7ed78';
export const FREE_PLAN_ID = 'price_1LnfByLgxGi7JA3GC49GOkRJ';
export const HAS_TRIALS = false;

// Free-plan trial for paid-only templates (`onlyForPaidPlans` presets): a
// logged-in Free user gets FREE_TEMPLATE_TRIAL_RUNS generations per template
// in the dashboard, then must upgrade. Enforced on the client (GenericComponent
// blur + upgrade overlay, trial tracked in localStorage) AND on the server
// (/api/generate counts the user's existing completions for the preset —
// localStorage is trivially reset). Was switched off 2026-08-17 (1aa5e4c23,
// "temporarily allow Free users to generate from paid-only templates") and
// re-enabled 2026-09-09 at Dmytro's request.
export const FREE_TEMPLATE_TRIAL_LIMIT_ENABLED = true;
export const FREE_TEMPLATE_TRIAL_RUNS = 1;

// Conversion experiment (2026-10-19, Dmytro): treat EVERY text template in the
// dashboard (GenericComponent) as paid-only, not just the `onlyForPaidPlans`
// ones, so the one-try trial above applies to all of them. Presets that say
// `onlyForPaidPlans: false` explicitly (clinical notes, diss track) keep
// opting out. Read it through `isPaidOnlyPreset` (@/lib/presetAccess); the
// server applies it only to requests sent by the template page
// (`fromTemplatePage`), so other /api/generate callers (transcript AI content,
// chat, editor, workflows, the API, the phone app) are unaffected. Set to
// false to end the experiment.
export const ALL_TEXT_TEMPLATES_PAID_ONLY = true;

// Anonymous landing-page forms (/api/public-generate + PublicTemplateForm):
// per visitor IP per rolling 24h, at most this many generations per template
// and this many across all templates. Lowered from 2 / 10 on 2026-09-09 to cut
// the cost of anonymous traffic (it was ~90% of the preset volume that made
// the August Vertex bill). The server is the real gate; the client mirrors
// the numbers for the pre-submit toasts. `language-detector` (note-taker) is
// exempt on the server.
export const PUBLIC_GENERATIONS_PER_PRESET_PER_DAY = 1;
export const PUBLIC_GENERATIONS_TOTAL_PER_DAY = 3;

// WaveSpeed video upscaling, verified 2026-09-05. Uses the same 50 credits/$
// cost-recovery basis as video extension. Round the total, not each second.
// https://wavespeed.ai/docs/docs-api/wavespeed-ai/video-upscaler
// https://wavespeed.ai/docs/docs-api/wavespeed-ai/video-upscaler-pro
// https://wavespeed.ai/docs/docs-api/wavespeed-ai/ultimate-video-upscaler
export const VIDEO_UPSCALE_CREDITS_PER_SECOND = {
  standard: { '720p': 0.25, '1080p': 0.25, '2k': 0.5, '4k': 1 },
  pro: { '720p': 2, '1080p': 2, '2k': 2.5, '4k': 3.5 },
  ultimate: { '720p': 1, '1080p': 1.5, '2k': 2.5, '4k': 4 },
} as const;

// MiniMax H3 regeneration: $0.05/output second, $0.05/reference-video
// second, and $0.025/image beyond the first five. Same 50 credits/$ basis.
// https://platform.minimax.io/docs/guides/pricing-paygo
export const MINIMAX_VIDEO_UPGRADE_CREDITS = {
  perSecond: 2.5,
  perExtraImage: 1.25,
} as const;

// Starter plan promo: STARTER50 — 50% off first month of Starter ($16 → $8)
export const STARTER_PROMO = {
  code: 'STARTER50',
  promotionCodeId: 'promo_1T4aCSLgxGi7JA3GtqTVewuO',
  productId: 'prod_MakjxDVwGdvoVK',
  discountPercent: 50,
  billingInterval: 'month' as const,
};

export const TRAIN_AI_MODEL_PRICE_ID = 'price_1PuywMLgxGi7JA3G9F9qxCNr';

export const HEADSHOTS_PRICES = [
  'price_1PqFiwLgxGi7JA3GBGrtEczG', // 19
  'price_1PqFjlLgxGi7JA3GyHjJraPV', // 29
  'price_1PqFjvLgxGi7JA3GqkIXCg96', // 49
];

export const products_for_home_page = [
  // 'prod_MWid5w4bCOrzic', //Free
  'prod_MakjxDVwGdvoVK', // Starter
  'prod_NtlgdYiAMxIUwD', //Unlimited
  'prod_QskSrexOhYVHhJ', //Unlimited 50
  // 'prod_OlgfpT7JJNOYtw', //Unlimited 200
  'prod_OqT3UM05zlYR3V', //Teams
];

export const products_for_free_plan = [
  'prod_QHeRbUODph0Qnk', //Unlimited 1000
  'prod_U7YQzkeIfsZcgI', //Unlimited 2000
  'prod_UFrxvYPuldnFnh', //Unlimited 5000
  'prod_PfsXrSTNjfI9RO', //Unlimited 500
  'prod_OlgfpT7JJNOYtw', //Unlimited 200
  // 'prod_OMz4MHdAs5HRUI', //Unlimited GPT-3.5
  'prod_SMsgVgYX3RM7mX', //Unlimited 350
  'prod_MakjxDVwGdvoVK', // Starter
  'prod_NtlgdYiAMxIUwD', //Unlimited
  // 'prod_Mr1T3qMOkgp5w9', //Basic
  'prod_QskSrexOhYVHhJ', //Unlimited 50
  'prod_OqT3UM05zlYR3V', //Teams
];

export const interior_designer_product_ids = [
  'prod_MakjxDVwGdvoVK', // Starter
  'prod_NtlgdYiAMxIUwD', // Unlimited
  'prod_SMsgVgYX3RM7mX', // Unlimited 350
];

export const upgrade_tools_product_ids = [
  // 'prod_OMz4MHdAs5HRUI', //Unlimited GPT-3.5
  'prod_MakjxDVwGdvoVK', // Starter
  'prod_OlgfpT7JJNOYtw', //Unlimited 200
  'prod_SMsgVgYX3RM7mX', //Unlimited 350
  'prod_NtlgdYiAMxIUwD', //Unlimited
  // 'prod_Mr1T3qMOkgp5w9',//Basic
  'prod_QskSrexOhYVHhJ', //Unlimited 50
  'prod_OqT3UM05zlYR3V', //Teams
  'prod_PVWIKnwi4YRVgm',
  'prod_PVVOGLWwc2Ih7r',
  'prod_PfsXrSTNjfI9RO',
  // 'prod_PgMnD7wruz8aUW',
  // 'prod_PgXHUmzjdFmuDS',
  // 'prod_Po6rCp6CXcKJJW',
];

export const upgrade_product_ids = [
  'prod_MakjxDVwGdvoVK', // Starter
  // 'prod_OMz4MHdAs5HRUI',
  'prod_OlgfpT7JJNOYtw',
  'prod_SMsgVgYX3RM7mX',
  'prod_NtlgdYiAMxIUwD',
  // 'prod_Mr1T3qMOkgp5w9',//Basic
  'prod_QskSrexOhYVHhJ', //Unlimited 50
  'prod_OqT3UM05zlYR3V',
  // 'prod_PVWIKnwi4YRVgm',
  // 'prod_PVVOGLWwc2Ih7r',
  'prod_PfsXrSTNjfI9RO',
  'prod_QHeRbUODph0Qnk',
  'prod_U7YQzkeIfsZcgI',
  // 'prod_PgMnD7wruz8aUW',
  // 'prod_PgXHUmzjdFmuDS',
  // 'prod_Po6rCp6CXcKJJW',
];

export const tts_product_ids = [
  'free_tts',
  // 'prod_PgMnD7wruz8aUW',
  // 'prod_PgXHUmzjdFmuDS',
  // 'prod_Po6rCp6CXcKJJW',
  'prod_MakjxDVwGdvoVK', // Starter
  'prod_OlgfpT7JJNOYtw', //Unlimited 200
  'prod_NtlgdYiAMxIUwD', //Unlimited
];

export const HAS_FREE_PLAN = true;

export const CAN_BUY_WORDS_IN_FREE_PLAN = false;

export const WORDS_PRICE_ID = 'price_1MJ5PpLgxGi7JA3GO8um3Nux';

export const NEW_WORDS_PRICE_ID = 'price_1MwNj4LgxGi7JA3GSJ7L0NDp';

export const EXTRA_WORDS_PRICE_ID = 'price_1TMkHVLgxGi7JA3GmAuiD2Jg';

export const IMAGES_PRICE_ID = 'price_1PYPf5LgxGi7JA3GuiSuCxJ8';

export const IMAGES_PRICE_COUNT = 100;

export const IMAGES_PRICE = 1200;

export const WORDS_PURCHASE_COUNT = 5000;

export const NEW_WORDS_PURCHASE_COUNT = 50000;

export const EXTRA_WORDS_PURCHASE_COUNT = 100000;

export const WORDS_PRICE = 299;

export const NEW_WORDS_PRICE = 1800;

export const FREE_AUDIOS_COUNT = 2;

export const TEAMS_SEATS_COUNT = 3;

// Additional seat prices for team plans (recurring monthly)
// TODO: Replace with actual Stripe Price IDs
export const ADDITIONAL_SEAT_PRICES = {
  Teams: 'price_1SUicjLgxGi7JA3GbUtmaVW8', // $23/month per seat
  'Teams 5 seats': 'price_1SUicjLgxGi7JA3GbUtmaVW8', // $20/month per seat
  'Teams 6 seats': 'price_1SUicjLgxGi7JA3GbUtmaVW8', // $19/month per seat
  'Teams 10 seats': 'price_1SUicjLgxGi7JA3GbUtmaVW8', // $18/month per seat
  'Teams 20 seats': 'price_1SUicjLgxGi7JA3GbUtmaVW8', // $16/month per seat
  'Teams 30 seats': 'price_1SUiXjLgxGi7JA3GRsoDuOCb', // $15/month per seat
  'Teams 40 seats': 'price_1SUiXjLgxGi7JA3GRsoDuOCb', // $14/month per seat
  'Teams 200': 'price_1SUiXjLgxGi7JA3GRsoDuOCb', // $12/month per seat
  'Teams 300': 'price_1SUiXjLgxGi7JA3GRsoDuOCb', // $10/month per seat
};

// Maximum additional seats allowed per plan
export const MAX_ADDITIONAL_SEATS = 50;

// Plans that support purchasing additional team seats
// The seat price will be the same as the subscription price (fetched dynamically from Stripe)
export const PLANS_WITH_TEAM_SEATS: string[] = [
  // Teams plans
  'Teams',
  'Teams 5 seats',
  'Teams 6 seats',
  'Teams 10 seats',
  'Teams 20 seats',
  'Teams 30 seats',
  'Teams 40 seats',
  'Teams 200',
  'Teams 300',
  // Individual plans
  'Unlimited 5000',
  'Unlimited 2000',
  'Unlimited 1000',
  'Unlimited 500',
  'Unlimited 350',
  'Unlimited 200',
  'Unlimited',
  'Pro 1000',
  'Pro 500',
  'Pro 400',
  'Pro 300',
  'Pro 200',
  'Pro',
  'Starter',
];

// Always-On Hosting add-on: keeps a published web app's Cloud Run service warm
// (min-instances=1, no cold starts). Billed per app as an extra item on the
// user's existing subscription; the price interval must match the plan's
// billing interval (Stripe forbids mixing intervals on one subscription).
// Stripe product "Always-On Hosting" (prod_UyKoJkDIxksKrK)
export const ALWAYS_ON_PRICES: Record<'month' | 'year', string> = {
  month: 'price_1TyO9KLgxGi7JA3G8wplHHlq', // $15/month per app
  year: 'price_1TyOASLgxGi7JA3Gg91kTw1Y', // $180/year per app
};

export const ALWAYS_ON_PRICE_IDS: string[] = Object.values(ALWAYS_ON_PRICES);

// Display prices for the UI (must match the Stripe prices above)
export const ALWAYS_ON_MONTHLY_PRICE_USD = 15;
export const ALWAYS_ON_YEARLY_PRICE_USD = 180;

// Safety cap on always-on apps per user
export const MAX_ALWAYS_ON_APPS = 20;

// Base seats per plan (Teams plans have more base seats, individual plans default to 1)
export const BASE_SEATS_PER_PLAN: Record<string, number> = {
  Teams: 3,
  'Teams 5 seats': 5,
  'Teams 6 seats': 6,
  'Teams 10 seats': 10,
  'Teams 20 seats': 20,
  'Teams 30 seats': 30,
  'Teams 40 seats': 40,
  'Teams 200': 3,
  'Teams 300': 3,
  // All individual plans default to 1 base seat (the owner) - not listed here
};

export const REPLICATE_IMAGE_GENERATION_VERSION =
  'ea1addaab376f4dc227f5368bbd8eff901820fd1cc14ed8cad63b29249e9d463';

export const REPLICATE_IMAGE_GENERATION_OLD_VERSION =
  '601eea49d49003e6ea75a11527209c4f510a93e2112c969d548fbb45b9c4f19f';

export const KANDINKSY_3_0_IMAGE_GENERATION_VERSION =
  '706edd88577cccfa826404969330e3d9121ad98ea3336acfcab8873a837070e2';

export const SDXL_VERSION =
  '5599ed30703defd1d160a25a63321b4dec97101d98b4674bcc56e41f62f35637';

export const EXTRA_FAST_WHISPER_MODEL =
  '3ab86df6c8f54c11309d4d1f930ac292bad43ace52d10c80d87eb258b3c9f79c';

export const IMAGE_TO_IMAGE_SHAPE_MODEL =
  '3bb13fe1c33c35987b33792b01b71ed6529d03f165d1c2416375859f09ca9fef';

export const TRANSFER_STYLE_IMAGE_MODEL =
  'f1023890703bc0a5a3a2c21b5e498833be5f6ef6e70e9daf6b9b3a4fd8309cf0';

/**
 * Models that reject the `temperature` parameter. Anthropic's Opus 4.8+ /
 * Fable / Sonnet 5 return a 400 ("`temperature` is deprecated for this
 * model."), and OpenAI's GPT-6 Astra rejects temperature/top_p/logprobs
 * outright. Omit `temperature` for these models and let the API use its default.
 */
export const MODELS_WITHOUT_TEMPERATURE: readonly string[] = [
  'gpt-6-astra',
  'gpt-6-astra-max',
  'claude-fable-5-1',
  'claude-fable-5-1-reasoning',
  'claude-fable-5-1-reasoning-low',
  'claude-fable-5-1-reasoning-medium',
  'claude-fable-5-1-reasoning-high',
  'claude-fable-5-1-reasoning-max',
  'claude-fable-5',
  'claude-fable-5-reasoning',
  'claude-fable-5-reasoning-low',
  'claude-fable-5-reasoning-medium',
  'claude-fable-5-reasoning-high',
  'claude-fable-5-reasoning-max',
  'claude-opus-4-8',
  // Opus 5 rejects sampling params (temperature/top_p/top_k) with a 400,
  // same as Opus 4.7/4.8 and Fable 5.
  'claude-opus-5-5',
  'claude-opus-5-5-reasoning',
  'claude-opus-5-5-reasoning-low',
  'claude-opus-5-5-reasoning-medium',
  'claude-opus-5-5-reasoning-high',
  'claude-opus-5-5-reasoning-max',
  'claude-opus-5',
  'claude-opus-5-reasoning',
  'claude-opus-5-reasoning-low',
  'claude-opus-5-reasoning-medium',
  'claude-opus-5-reasoning-high',
  'claude-opus-5-reasoning-max',
  // Sonnet 5 rejects sampling params (temperature/top_p/top_k) with a 400,
  // same as Opus 4.7/4.8 and Fable 5. Sonnet 5.5 rejects non-default values.
  'claude-sonnet-5-5',
  'claude-sonnet-5-5-reasoning',
  'claude-sonnet-5-5-reasoning-low',
  'claude-sonnet-5-5-reasoning-medium',
  'claude-sonnet-5-5-reasoning-high',
  'claude-sonnet-5-5-reasoning-max',
  'claude-sonnet-5',
  'claude-sonnet-5-reasoning',
  // Haiku 5.5 accepts only temperature 1 (the default); any other value 400s.
  'claude-haiku-5-5',
  'claude-haiku-5-5-reasoning',
  'claude-haiku-5-5-reasoning-low',
  'claude-haiku-5-5-reasoning-medium',
  'claude-haiku-5-5-reasoning-high',
  'claude-haiku-5-5-reasoning-max',
];

/** Whether the given model accepts an explicit `temperature` parameter. */
export const modelSupportsTemperature = (model?: string | null): boolean =>
  !model || !MODELS_WITHOUT_TEMPERATURE.includes(model);

// Free-plan limits for Agents (bots). Raised so free users can actually train
// an agent on their own website before deciding to pay — the spend backstop is
// the 100 messages/month cap enforced (by blocking every bot of the owner) in
// /api/notify-about-messages-limit-for-free-bots, not the training limits.
export const FREE_PLAN_BOT_LIMITS = {
  // Pages fetched per website-crawler source. The queue controllers clamp every
  // plan to 100 (crawl.controller / firecrawl.controller), so this is the
  // ceiling for everyone — free users simply no longer sit below it.
  crawlLinks: 100,
  // Training sources per agent. MUST stay well above `crawlLinks`: a crawl or
  // sitemap import creates one child BotSource per fetched page (plus the
  // parent row), and /api/notify-about-source-limit-for-free-bots pauses any
  // free agent whose BotSource count exceeds this number.
  sources: 250,
  // Files accepted per upload batch (paid plans allow 10).
  filesPerUpload: 10,
};

export const BOTS_IN_PLAN = {
  Free: 1,
  'Unlimited GPT-3.5': 2,
  'Unlimited 50': 3,
  Unlimited: 4,
  'Unlimited 200': 8,
  'Unlimited 350': 12,
  'Unlimited 500': 16,
  'Unlimited 1000': 32,
  'Unlimited 2000': 64,
  'Unlimited 5000': 160,
  'Starter TTS': 1,
  'TTS 250': 1,
  'Pro TTS': 1,
  'TTS Basic': 1,
  'TTS Starter': 1,
  Enterprise: 320,
  Teams: 8,
  'Teams 5 seats': 8,
  'Teams 6 seats': 8,
  'Teams 10 seats': 10,
  'Teams 20 seats': 32,
  'Teams 30 seats': 64,
  'Teams 40 seats': 40,
  'Teams 200': 8,
  'Teams 300': 16,
  'Unlimited GPT4': 32,
  Pro: 4,
  'Pro 200': 8,
  'Pro 300': 16,
  'Pro 400': 20,
  'Pro 500': 16,
  'Pro 1000': 32,
  'Pro Test': 2,
  Starter: 2,
  Basic: 1,
  'TTS Free': 1,
  AppSumo: 2,
  'Lifetime Pro': 8,
  'Lifetime Pro 150': 8,
  'Lifetime Pro 200': 8,
  'Lifetime Pro 400': 16,
  'Lifetime Starter': 2,
};

// Plans where token overuse is monitored by the
// notify-about-token-limit-exceeded cron: warning email at
// TOKEN_LIMIT_WARNING_PERCENTAGE, profiles.is_blocked set at
// TOKEN_LIMIT_BLOCK_PERCENTAGE (unblocked by upgrading or purchasing enough
// bonus words). One shared plan list so nobody is blocked without having been
// warned first.
export const TOKEN_LIMIT_ENFORCED_PLANS = [
  'Unlimited',
  'Unlimited 50',
  'Unlimited 200',
  'Unlimited 350',
  'Unlimited 500',
  'Unlimited 1000',
  'Unlimited 2000',
  'Unlimited 5000',
  'Unlimited GPT-3.5',
  'Teams',
  'Pro',
  'Starter',
];

export const TOKEN_LIMIT_WARNING_PERCENTAGE = 800;
export const TOKEN_LIMIT_BLOCK_PERCENTAGE = 5000;

export const TOKENS_IN_PLAN = {
  Unlimited: 5100000,
  'Unlimited 50': 3000000,
  'Unlimited 200': 10000000,
  'Unlimited 350': 15000000,
  'Unlimited 500': 20000000,
  'Unlimited 1000': 50000000,
  'Unlimited 2000': 100000000,
  'Unlimited 5000': 250000000,
  'Unlimited GPT-3.5': 1500000,
  'Starter TTS': 3000,
  'TTS 250': 3000,
  'Pro TTS': 3000,
  'TTS Basic': 3000,
  'TTS Starter': 3000,
  Enterprise: 1000000,
  Teams: 5100000,
  'Teams 5 seats': 5100000,
  'Teams 6 seats': 5000000,
  'Teams 10 seats': 5000000,
  'Teams 20 seats': 5000000,
  'Teams 30 seats': 5000000,
  'Teams 40 seats': 5500000,
  'Teams 200': 200000,
  'Teams 300': 300000,
  'Unlimited GPT4': Infinity,
  Pro: 5100000,
  'Pro 200': 200000,
  'Pro 300': 300000,
  'Pro 400': 400000,
  'Pro 500': 500000,
  'Pro 1000': 1000000,
  'Pro Test': 25000,
  Starter: 1500000,
  Basic: 400000,
  Free: 3000,
  'TTS Free': 3000,
  AppSumo: 15000,
  'Lifetime Pro': 5100000,
  'Lifetime Pro 150': 150000,
  'Lifetime Pro 200': 200000,
  'Lifetime Pro 400': 400000,
  'Lifetime Starter': 20000,
};

// TODO: improve this
export const WORDS_IN_PLAN = {
  'Starter TTS': 2000,
  'TTS 250': 3000,
  'Pro TTS': 3000,
  'TTS Basic': 3000,
  'TTS Starter': 3000,
  Enterprise: 1000000,
  Teams: 100000,
  'Teams 5 seats': 100000,
  'Teams 6 seats': 100000,
  'Teams 10 seats': 100000,
  'Teams 20 seats': 100000,
  'Teams 30 seats': 100000,
  'Teams 40 seats': 150000,
  'Teams 200': 200000,
  'Teams 300': 300000,
  'Unlimited 50': 50000,
  Unlimited: 100000,
  'Unlimited 200': 200000,
  'Unlimited 350': 350000,
  'Unlimited 500': 500000,
  'Unlimited 1000': 1000000,
  'Unlimited 2000': 2000000,
  'Unlimited 5000': 5000000,
  'Unlimited GPT-3.5': 25000,
  'Unlimited GPT4': Infinity,
  Pro: 100000,
  'Pro 200': 200000,
  'Pro 300': 300000,
  'Pro 400': 400000,
  'Pro 500': 500000,
  'Pro 1000': 1000000,
  'Pro Test': 25000,
  Starter: 25000,
  Basic: 10000,
  Free: 2000,
  'TTS Free': 3000,
  AppSumo: 15000,
  'Lifetime Pro': 100000,
  'Lifetime Pro 150': 150000,
  'Lifetime Pro 200': 200000,
  'Lifetime Pro 400': 400000,
  'Lifetime Starter': 20000,
};

// Maximum share of a user's monthly image/video credit allowance that can be
// refunded via downvotes within a single billing period. Bounds how much a user
// can claw back by giving generations a thumbs-down (abuse protection).
export const MAX_REFUND_RATIO = 0.1;

export const IMAGES_IN_PLAN = {
  'Starter TTS': 5,
  'TTS 250': 5,
  'Pro TTS': 5,
  'TTS Basic': 5,
  'TTS Starter': 5,
  Enterprise: 1000,
  'Unlimited GPT4': Infinity,
  Teams: 400,
  'Teams 5 seats': 400,
  'Teams 6 seats': 400,
  'Teams 10 seats': 400,
  'Teams 20 seats': 500,
  'Teams 30 seats': 500,
  'Teams 40 seats': 500,
  'Teams 200': 800,
  'Teams 300': 1250,
  Unlimited: 500,
  'Unlimited 50': 300,
  'Unlimited 200': 800,
  'Unlimited 350': 1250,
  'Unlimited 500': 2000,
  'Unlimited 1000': 5000,
  'Unlimited 2000': 10000,
  'Unlimited 5000': 25000,
  'Unlimited GPT-3.5': 150,
  Pro: 350,
  'Pro 200': 200,
  'Pro 300': 300,
  'Pro 400': 400,
  'Pro 500': 1500,
  'Pro 1000': 3000,
  'Pro Test': 250,
  Starter: 200,
  Basic: 75,
  Free: 3,
  'TTS Free': 5,
  AppSumo: 150,
  'Lifetime Pro': 100,
  'Lifetime Pro 150': 150,
  'Lifetime Pro 200': 200,
  'Lifetime Pro 400': 900,
  'Lifetime Starter': 50,
};

// During a 7-day Stripe trial the plan's image credits are capped at this
// value (words/features stay at full plan level). Image/video generations are
// the expensive per-unit cost, so an uncapped trial of e.g. Unlimited 1000
// (5000 credits) would be an easy abuse vector.
export const TRIAL_IMAGE_CREDITS_LIMIT = 50;

// Resolve the monthly image-credit allowance for a subscription. Pass the
// subscription row's `status` so trialing subscriptions get the capped
// allowance; bonus images are added by callers on top of this.
export const getImagesInPlan = (
  subscriptionName: string,
  subscriptionStatus?: string | null,
): number => {
  const base = Number(
    IMAGES_IN_PLAN[subscriptionName as keyof typeof IMAGES_IN_PLAN] ??
      IMAGES_IN_PLAN.Free,
  );
  if (subscriptionStatus === 'trialing') {
    return Math.min(base, TRIAL_IMAGE_CREDITS_LIMIT);
  }
  return base;
};

// Flat Seed Audio provider charge, billed from the existing audio allowance.
export const SOUND_EFFECT_CHARACTERS_PER_GENERATION = 1000;

export const SPEECHES_WORDS_IN_PLAN = {
  'Starter TTS': 50000,
  'TTS 250': 250000,
  'Pro TTS': 60000,
  'TTS Basic': 30000,
  'TTS Starter': 60000,
  Enterprise: 30000,
  Teams: 75000,
  'Teams 5 seats': 75000,
  'Teams 6 seats': 75000,
  'Teams 10 seats': 75000,
  'Teams 20 seats': 75000,
  'Teams 30 seats': 75000,
  'Teams 40 seats': 75000,
  'Teams 200': 150000,
  'Teams 300': 200000,
  'Unlimited 50': 50000,
  Unlimited: 75000,
  'Unlimited 200': 120000,
  'Unlimited 350': 200000,
  'Unlimited 500': 300000,
  'Unlimited 1000': 700000,
  'Unlimited 2000': 1400000,
  'Unlimited 5000': 3500000,
  'Unlimited GPT-3.5': 10000,
  'Unlimited GPT4': 20000,
  Pro: 20000,
  'Pro 200': 20000,
  'Pro 300': 30000,
  'Pro 400': 10000,
  'Pro 500': 50000,
  'Pro 1000': 100000,
  'Pro Test': 25000,
  Starter: 25000,
  Basic: 3500,
  Free: 100,
  'TTS Free': 1000,
  AppSumo: 15000,
  'Lifetime Pro': 10000,
  'Lifetime Pro 150': 15000,
  'Lifetime Pro 200': 20000,
  'Lifetime Pro 400': 40000,
  'Lifetime Starter': 20000,
};

export const AUDIOS_IN_PLAN = {
  'Starter TTS': 2,
  'TTS 250': 2,
  'Pro TTS': 2,
  'TTS Basic': 2,
  'TTS Starter': 2,
  Enterprise: 100,
  Teams: 1000,
  'Teams 5 seats': 1000,
  'Teams 6 seats': 1000,
  'Teams 10 seats': 1000,
  'Teams 20 seats': 1000,
  'Teams 30 seats': 1000,
  'Teams 40 seats': 1000,
  'Teams 200': 2000,
  'Teams 300': 3000,
  Unlimited: 300,
  'Unlimited 50': 30,
  'Unlimited 200': 600,
  'Unlimited 350': 800,
  'Unlimited 500': 1000,
  'Unlimited 1000': 2000,
  'Unlimited 2000': 4000,
  'Unlimited 5000': 10000,
  'Unlimited GPT-3.5': 20,
  'Unlimited GPT4': 100,
  Pro: 50,
  'Pro 200': 200,
  'Pro 300': 300,
  'Pro 400': 400,
  'Pro 500': 500,
  'Pro 1000': 600,
  'Pro Test': 100,
  Starter: 25,
  Basic: 8,
  Free: 2,
  'TTS Free': 2,
  AppSumo: 15000,
  'Lifetime Pro': 50,
  'Lifetime Pro 150': 100,
  'Lifetime Pro 200': 150,
  'Lifetime Pro 400': 200,
  'Lifetime Starter': 25,
};

// Discount automatically applied to standalone one-time credit purchases.
// Must match a Stripe coupon in DISCOUNT_COUPONS (create-checkout-session-product.ts).
export const CREDIT_PURCHASE_DISCOUNT_PERCENT = 30;

export const getDiscountedCreditPrice = (price: number): number =>
  Math.round(price * (100 - CREDIT_PURCHASE_DISCOUNT_PERCENT)) / 100;

// Credit price tiers for both images and videos
export const CREDIT_PRICES = {
  TIER_1: {
    credits: 100,
    priceId: 'price_1PYPf5LgxGi7JA3GuiSuCxJ8',
    price: 12,
  },
  TIER_2: {
    credits: 500,
    priceId: 'price_1RF8ISLgxGi7JA3GcYZEN3oj',
    price: 55,
  },
  // TIER_3: {
  //   credits: 1000,
  //   priceId: 'price_1Qqwp1LgxGi7JA3GQH4DFPiV',
  //   price: 48,
  // },
} as const;

// Domain purchase configuration
export const DOMAIN_PURCHASE_MARGIN_CENTS = Number(
  process.env.DOMAIN_PURCHASE_MARGIN_CENTS || 500,
); // $5 margin on top of registrar cost

export const SUPPORTED_TLDS = [
  { tld: 'com', label: '.com' },
  { tld: 'net', label: '.net' },
  { tld: 'org', label: '.org' },
  { tld: 'io', label: '.io' },
  { tld: 'dev', label: '.dev' },
  { tld: 'app', label: '.app' },
  { tld: 'co', label: '.co' },
  { tld: 'xyz', label: '.xyz' },
  { tld: 'ai', label: '.ai' },
  { tld: 'site', label: '.site' },
] as const;

// Character limits for knowledge base per plan
export const CHARACTERS_IN_PLAN = {
  // Sized to hold a ~100-page crawl (see FREE_PLAN_BOT_LIMITS.crawlLinks);
  // the old 400k cap blocked free users mid-way through training on their site.
  Free: 1000000,
  'Unlimited GPT-3.5': 5000000,
  'Unlimited 50': 5000000,
  Unlimited: 10000000,
  'Unlimited 200': 20000000,
  'Unlimited 350': 30000000,
  'Unlimited 500': 40000000,
  'Unlimited 1000': 50000000,
  'Unlimited 2000': 100000000,
  'Unlimited 5000': 250000000,
  'Starter TTS': 400000,
  'TTS 250': 400000,
  'Pro TTS': 400000,
  'TTS Basic': 400000,
  'TTS Starter': 400000,
  Enterprise: 100000000,
  Teams: 8000000,
  'Teams 5 seats': 8000000,
  'Teams 6 seats': 8000000,
  'Teams 10 seats': 10000000,
  'Teams 20 seats': 20000000,
  'Teams 30 seats': 30000000,
  'Teams 40 seats': 40000000,
  'Teams 200': 20000000,
  'Teams 300': 30000000,
  'Unlimited GPT4': 40000000,
  Pro: 4000000,
  'Pro 200': 8000000,
  'Pro 300': 12000000,
  'Pro 400': 16000000,
  'Pro 500': 20000000,
  'Pro 1000': 40000000,
  'Pro Test': 1000000,
  Starter: 5000000,
  Basic: 400000,
  'TTS Free': 400000,
  AppSumo: 1000000,
  'Lifetime Pro': 4000000,
  'Lifetime Pro 150': 6000000,
  'Lifetime Pro 200': 8000000,
  'Lifetime Pro 400': 16000000,
  'Lifetime Starter': 1000000,
};

// Actions allowed per bot for each plan
export const ACTIONS_IN_PLAN = {
  // Actions are a paid feature — Free users see the 7-day-trial gate instead.
  Free: 0,
  'Unlimited GPT-3.5': 2,
  'Unlimited 50': 5,
  Unlimited: 8,
  'Unlimited 200': 16,
  'Unlimited 350': 32,
  'Unlimited 500': 64,
  'Unlimited 1000': 128,
  'Unlimited 2000': 256,
  'Unlimited 5000': 640,
  'Starter TTS': 0,
  'TTS 250': 0,
  'Pro TTS': 0,
  'TTS Basic': 0,
  'TTS Starter': 0,
  Enterprise: 7,
  Teams: 3,
  'Teams 5 seats': 3,
  'Teams 6 seats': 3,
  'Teams 10 seats': 3,
  'Teams 20 seats': 5,
  'Teams 30 seats': 10,
  'Teams 40 seats': 10,
  'Teams 200': 5,
  'Teams 300': 10,
  'Unlimited GPT4': 20,
  Pro: 3,
  'Pro 200': 5,
  'Pro 300': 5,
  'Pro 400': 10,
  'Pro 500': 10,
  'Pro 1000': 20,
  'Pro Test': 1,
  Starter: 3,
  Basic: 0,
  'TTS Free': 0,
  AppSumo: 1,
  'Lifetime Pro': 3,
  'Lifetime Pro 150': 3,
  'Lifetime Pro 200': 5,
  'Lifetime Pro 400': 10,
  'Lifetime Starter': 1,
};

// List of developing countries eligible for Basic plan
export const DEVELOPING_COUNTRIES = [
  'IN', // India
  'PH', // Philippines
  // 'ZA', // South Africa
  'BR', // Brazil
  'MX', // Mexico
  'ID', // Indonesia
  'VN', // Vietnam
  'NG', // Nigeria
  'KE', // Kenya
  'EG', // Egypt
  'AR', // Argentina
  'CO', // Colombia
  'PE', // Peru
  'TH', // Thailand
  // 'MY', // Malaysia
  'UK', // Ukraine
  'RO', // Romania
  'BG', // Bulgaria
  'GR', // Greece
  'CZ', // Czechia
  'PL', // Poland
  // 'MY', // Malaysia
  'HU', // Hungary
];

// List of developed countries where users can upload images for free
export const DEVELOPED_COUNTRIES = [
  'US', // United States
  'CA', // Canada
  'AU', // Australia
  'DE', // Germany
  'FI', // Finland
  'FR', // France
  'GB', // United Kingdom
  'AE', // United Arab Emirates
  'SA', // Saudi Arabia
  'QA', // Qatar
  'KW', // Kuwait
  'BH', // Bahrain
  'OM', // Oman
  'JP', // Japan
  'KR', // South Korea
  'SG', // Singapore
  'NZ', // New Zealand
  'CH', // Switzerland
  'SE', // Sweden
  'NO', // Norway
  'DK', // Denmark
  'NL', // Netherlands
  'BE', // Belgium
  'AT', // Austria
  'IE', // Ireland
  'LU', // Luxembourg
  'IS', // Iceland
];

// Dubbing: 10 credits per minute (treated as 2000 "words" for credit calculation)
export const DUBBING_CREDITS_PER_MINUTE = 10;
// Convert seconds to "words" for credit tracking (1 minute = 2000 "words")
export const DUBBING_SECONDS_AS_WORDS_MULTIPLIER = 100 / 3; // 33.33 chars per second = 2000 chars per minute
// Hard per-job ceiling for dubbing sources (the advertised "up to 2.5 hours").
export const DUBBING_MAX_SECONDS = 2.5 * 60 * 60;

// Voice Changer is metered against the shared speech word pool at the same
// per-second rate as dubbing.
export const VOICE_CHANGER_SECONDS_AS_WORDS_MULTIPLIER = 100 / 3;
// Hard per-job ceiling for Voice Changer input audio, independent of plan —
// longer files would also blow the API route's 300s execution budget.
export const VOICE_CHANGER_MAX_SECONDS = 30 * 60;

// Image models available in chat for inline image generation
export const chatImageModels = [
  {
    id: 'Nano Banana 2.1',
    label: 'Nano Banana 2.1',
    credits: NANO_BANANA_2_1_CREDITS['1K'],
  },
  {
    id: 'Nano Banana 2 Lite',
    label: 'Nano Banana 2 Lite',
    credits: NANO_BANANA_2_LITE_CREDITS,
  },
  {
    id: 'Nano Banana 2',
    label: 'Nano Banana 2',
    credits: NANO_BANANA_2_CREDITS['1K'],
  },
  {
    id: 'Nano Banana Pro',
    label: 'Nano Banana Pro',
    credits: NANO_BANANA_PRO_CREDITS['1K'],
  },
  { id: 'Seedream 4.5', label: 'Seedream 4.5', credits: 2 },
  { id: 'Z-Image Turbo', label: 'Z-Image Turbo', credits: 0.1 },
  {
    id: 'OpenAI GPT Image 2 Low',
    label: 'GPT Image 2 Low',
    credits: 0.5,
  },
  {
    id: 'OpenAI GPT Image 2 Medium',
    label: 'GPT Image 2 Medium',
    credits: 3,
  },
  {
    id: 'OpenAI GPT Image 2 High',
    label: 'GPT Image 2 High',
    credits: 11,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare Low',
    label: 'GPT Image 2.5 Flare Low',
    credits: 0.5,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare Medium',
    label: 'GPT Image 2.5 Flare Medium',
    credits: 3,
  },
  {
    id: 'OpenAI GPT Image 2.5 Flare High',
    label: 'GPT Image 2.5 Flare High',
    credits: 11,
  },
];

// Max custom actors per subscription tier
export const MAX_CUSTOM_ACTORS = {
  Free: 1,
  'Unlimited GPT-3.5': 1,
  'Unlimited 50': 2,
  Unlimited: 4,
  'Unlimited 200': 8,
  'Unlimited 350': 16,
  'Unlimited 500': 32,
  'Unlimited 1000': 64,
  'Unlimited 2000': 128,
  'Unlimited 5000': 320,
  'Starter TTS': 0,
  'TTS 250': 0,
  'Pro TTS': 0,
  'TTS Basic': 0,
  'TTS Starter': 0,
  Enterprise: 7,
  Teams: 5,
  'Teams 5 seats': 3,
  'Teams 6 seats': 3,
  'Teams 10 seats': 3,
  'Teams 20 seats': 5,
  'Teams 30 seats': 10,
  'Teams 40 seats': 10,
  'Teams 200': 5,
  'Teams 300': 10,
  'Unlimited GPT4': 20,
  Pro: 3,
  'Pro 200': 5,
  'Pro 300': 5,
  'Pro 400': 10,
  'Pro 500': 10,
  'Pro 1000': 20,
  'Pro Test': 1,
  Starter: 2,
  Basic: 1,
  'TTS Free': 0,
  AppSumo: 1,
  'Lifetime Pro': 3,
  'Lifetime Pro 150': 3,
  'Lifetime Pro 200': 5,
  'Lifetime Pro 400': 10,
  'Lifetime Starter': 1,
};

// Free-bot signup-cohort detector (/api/cron/detect-free-bot-signup-cohorts,
// hourly). The per-account Free-bot cap (100 messages / 30 days) is trivially
// sharded: on 2026-09-20/21 a ring of 794 throwaway accounts from one signup
// wave ran Claude Opus through their own bots at 1-7 messages each. A cohort
// = Free accounts created on the same UTC day that all chatted through bots
// within the window. Auto-block needs all three strong signals (many accounts,
// a real message volume, and the huge prompts of a raw-model proxy — normal
// Free bot chats average a few thousand prompt tokens); weaker matches are
// only reported to support.
export const FREE_BOT_COHORT_WINDOW_HOURS = 24;
export const FREE_BOT_COHORT_WARN_MIN_ACCOUNTS = 10;
export const FREE_BOT_COHORT_BLOCK_MIN_ACCOUNTS = 25;
// 100 = one Free account's monthly allowance burned by the cohort in a day;
// the Sep 2026 ring ran ~2 messages per account per day across ~140 accounts.
export const FREE_BOT_COHORT_BLOCK_MIN_MESSAGES = 100;
export const FREE_BOT_COHORT_BLOCK_MIN_AVG_PROMPT_TOKENS = 20000;
